import { getContext, setContext } from 'svelte';
import type { Readable } from 'svelte/store';
import { GrpcWebFetchTransport } from '@protobuf-ts/grpcweb-transport';
import {
  UnaryCall,
  type NextUnaryFn,
  type RpcInterceptor,
  type RpcOptions,
} from '@protobuf-ts/runtime-rpc';
import { currentWritable } from '@threlte/core';

import { InternalEnvironmentServiceClient } from '@viamrobotics/web-client/environment/v1/environment.client';

import { browser } from '$app/environment';
import { isUnauthenticatedError } from '$lib/api/errors';

import { buildAuthInterceptor } from './auth-interceptor';
import {
  type AuthClient,
  type AuthState,
  AuthStatus,
  createAuthClient,
  initializeClient,
} from './client';
import { getAuthClientConfig } from './config';
import {
  isMissingTokenError,
  isNotLoggedInError,
  MissingTokenError,
  NotLoggedInError,
} from './errors';
import type { UserInfo } from './user-info';
import { isDebugLoggingEnabled } from '$lib/api/debug-logging';

export interface AuthContext {
  authState: Readable<AuthState>;
  initialize: () => Promise<AuthState>;
  requireLogin: () => void;
  login: () => unknown;
  logout: () => unknown;
  getCurrentlyStoredAccessToken: () => string | undefined;
  getAccessTokenForRequestWithRefresh: () => Promise<string>;
  getUserInfo: () => UserInfo | undefined;
  getRPCOptions: GetRPCOptions;
  getHTTPOptions: GetHTTPOptions;
  getOptionalRPCOptions: GetRPCOptions;
  /**
   * Transport-level gRPC interceptor that attaches the current Bearer token to
   * every unary and server-streaming call and refreshes-and-retries unary calls
   * once on UNAUTHENTICATED. Install on the `GrpcWebFetchTransport` so service
   * clients pick up auth automatically and call sites don't need to thread
   * `rpcOptions` through every request.
   *
   * No-op while auth state isn't `LoggedIn` (so the pre-auth environment
   * bootstrap still goes through unchanged). Streaming calls do a proactive
   * pre-request token refresh but do not attempt mid-stream retry on
   * UNAUTHENTICATED — restarting an in-flight stream cleanly is out of scope
   * here, and matches existing per-call behavior.
   */
  createAuthInterceptor: () => RpcInterceptor;
}

export type GetRPCOptions = () => Promise<RpcOptions>;

export type GetHTTPOptions = () => Promise<RequestInit>;

const AuthContextKey = Symbol('auth');

export const createAuthContext = (): AuthContext => {
  const authState = currentWritable<AuthState>({ status: AuthStatus.Loading });
  let client: AuthClient | undefined;

  /**
   * Guards against a competing logout() firing after login() has already
   * navigated the browser to /login.  Without this, in-flight prefetch calls
   * (e.g. prefetchAppUser) can detect an unauthenticated state and trigger
   * logout(), which races through FusionAuth's logout endpoint back to "/",
   * overwrites the session's backto URL, and prevents a user from going directly
   * to a non-landing page URL (billing page, organization settings, etc.).
   *
   * We don't need to reset it: once window.location.assign('/login?...') fires,
   * the page is about to unload, so there is no valid reason to allow a logout
   * afterward.
   */
  let loginInProgress = false;

  const initialize = async (): Promise<AuthState> => {
    if (browser) {
      let nextState: AuthState = { status: AuthStatus.LoggedOut };
      const config = getAuthClientConfig(window.location.hostname);
      const envClient = new InternalEnvironmentServiceClient(
        new GrpcWebFetchTransport({ baseUrl: window.location.origin })
      );

      const { response } = await envClient.getEnvironment({});
      if (response.environment?.authConfig) {
        const { clientId, audience } = response.environment.authConfig;
        config.clientId = clientId;
        config.audience = audience;
      }

      client = createAuthClient({
        clientID: config.clientId,
        audience: config.audience,
      });

      // Check for an authorization code in the URL (from a cross-app magic link).
      // If present, exchange it for fresh tokens before normal auth initialization.
      const authCode = extractAndStripAuthCode();
      let authCodeExchanged = false;

      if (authCode) {
        try {
          await client.exchangeAuthCode(authCode);
          authCodeExchanged = true;
        } catch {
          // Exchange failed (expired, already used, or invalid code).
          // Fall through to normal auth initialization.
          // eslint-disable-next-line no-console
          console.warn('Auth code exchange failed');
        }
      }

      try {
        if (authCodeExchanged) {
          // Token was stored by exchangeAuthCode. Check login state directly
          // rather than going through getAndCacheToken which calls /token.
          // The server-side session was created during the exchange, so
          // subsequent page loads will work normally via initializeClient.
          const user = client.getUserInfo();
          nextState = user
            ? { status: AuthStatus.LoggedIn, user }
            : { status: AuthStatus.LoggedOut };
        } else {
          nextState = await initializeClient(client);
        }
      } catch (error) {
        // TODO(mc, 2023-07-25): log to Sentry, not console
        // eslint-disable-next-line no-console
        console.error('Unknown error initializing auth', error);
      }

      authState.set(nextState);
    }

    return authState.current;
  };

  const requireLogin = () => {
    if (!client) {
      return;
    }

    if (authState.current.status === AuthStatus.LoggedOut) {
      loginInProgress = true;
      client.login();
    }
  };

  const getAccessToken = () => {
    if (!client) {
      return undefined;
    }

    if (!client.isLoggedIn()) {
      throw new NotLoggedInError();
    }

    const token = client.tokenStorage.retrieveToken();
    if (!token) {
      throw new MissingTokenError();
    }

    return token.accessToken;
  };

  const getUserInfo = () => client?.getUserInfo();

  /**
   * In-flight refresh promise. Used to deduplicate concurrent refresh attempts
   * so that only one /refresh request is made at a time.
   */
  let refreshPromise: Promise<void> | undefined;

  /** Start a token refresh if one isn't already in-flight, and return the shared promise. */
  const startRefresh = async (): Promise<void> => {
    if (!client) {
      return;
    }
    refreshPromise ??= client
      .refreshAccessToken()
      .then(() => undefined)
      .finally(() => {
        refreshPromise = undefined;
      });

    return refreshPromise;
  };

  const logout = (): void => {
    if (!client || loginInProgress) {
      return;
    }
    authState.set({ status: AuthStatus.Loading });
    client.logout();
  };

  /**
   * Get the user's access token for an API request.
   *
   * Proactively refreshes if the current token expires within the refresh
   * threshold. If the refresh fails but the token is still technically valid,
   * we swallow the error and let the request proceed. If the token is fully
   * expired and the refresh also failed, we throw NotLoggedInError.
   */
  const getAccessTokenForRequest = async (): Promise<string> => {
    if (!client || authState.current.status !== AuthStatus.LoggedIn) {
      throw new NotLoggedInError();
    }

    if (client.isTokenExpiringSoon()) {
      try {
        await startRefresh();
      } catch (error) {
        // only handle errors if the client is not logged in
        // refresh can fail and the client is still logged in
        // (premature refresh)
        if (!client.isLoggedIn()) {
          if (isNotLoggedInError(error)) {
            // only logout if the error is confirmed not logged in
            // and ignore errors from network, etc
            logout();
          }
          throw error;
        }
      }
    }

    const token = getAccessToken();
    if (!token) {
      throw new MissingTokenError();
    }

    return token;
  };

  /** Get gRPC request options for an endpoint that doesn't require auth. */
  const getOptionalRPCOptions = async (): Promise<RpcOptions> => {
    if (!client || authState.current.status !== AuthStatus.LoggedIn) {
      return {};
    }

    return getRPCOptions();
  };

  const login = (): void => {
    if (client) {
      const user = client.getUserInfo();
      if (user) {
        authState.set({ status: AuthStatus.LoggedIn, user });
      } else {
        loginInProgress = true;
        client.login();
      }
    }
  };

  /**
   * Get auth'd request options for an HTTP request.
   *
   * Proactively refreshes the access token if it is expiring soon.
   */
  const getHTTPOptions = async (): Promise<RequestInit> => {
    try {
      const token = await getAccessTokenForRequest();
      const headers: Record<string, string> = {
        Authorization: `Bearer ${token}`,
      };

      if (isDebugLoggingEnabled()) {
        headers['X-Viam-Debug'] = '1';
      }

      return { headers };
    } catch (error) {
      if (isMissingTokenError(error) || isNotLoggedInError(error)) {
        logout();
      }
      throw error;
    }
  };

  /**
   * Get auth'd request options for a gRPC request.
   *
   * Proactively refreshes the access token if it is expiring soon. On
   * UNAUTHENTICATED errors, attempts a token refresh before falling back to
   * logout.
   */
  const getRPCOptions = async (): Promise<RpcOptions> => {
    try {
      const token = await getAccessTokenForRequest();
      const meta: Record<string, string> = { Authorization: `Bearer ${token}` };

      if (isDebugLoggingEnabled()) {
        meta['x-viam-debug'] = '1';
      }

      return {
        meta,
        interceptors: [
          {
            interceptUnary: (next: NextUnaryFn, method, input, options) => {
              const rpcCall = next(method, input, options);

              // On UNAUTHENTICATED, refresh the token and transparently retry the call.
              const retryableResponse = rpcCall.response.catch(
                async (error: unknown) => {
                  if (!isUnauthenticatedError(error)) {
                    throw error;
                  }

                  try {
                    await startRefresh();
                  } catch (refreshError) {
                    if (isNotLoggedInError(refreshError)) {
                      logout();
                    }
                    throw error;
                  }

                  const newToken = getAccessToken();
                  if (!newToken) {
                    logout();
                    throw error;
                  }

                  const retryMeta: Record<string, string> = {
                    ...options.meta,
                    Authorization: `Bearer ${newToken}`,
                  };

                  if (isDebugLoggingEnabled()) {
                    retryMeta['x-viam-debug'] = '1';
                  }

                  const retryCall = next(method, input, {
                    ...options,
                    // Use the new token for the retry.
                    meta: retryMeta,
                  });
                  return retryCall.response;
                }
              );

              return new UnaryCall(
                rpcCall.method,
                rpcCall.requestHeaders,
                rpcCall.request,
                rpcCall.headers,
                retryableResponse,
                rpcCall.status,
                rpcCall.trailers
              );
            },
          },
        ],
      };
    } catch (error) {
      if (isMissingTokenError(error) || isNotLoggedInError(error)) {
        logout();
      }
      throw error;
    }
  };

  const createAuthInterceptor = (): RpcInterceptor =>
    buildAuthInterceptor({
      isLoggedIn: () =>
        Boolean(client) && authState.current.status === AuthStatus.LoggedIn,
      getAccessTokenForRequest,
      getAccessToken,
      startRefresh,
      logout,
    });

  return {
    authState,
    initialize,
    requireLogin,
    login,
    logout,
    getCurrentlyStoredAccessToken: getAccessToken,
    getAccessTokenForRequestWithRefresh: getAccessTokenForRequest,
    getUserInfo,
    getRPCOptions,
    getOptionalRPCOptions,
    getHTTPOptions,
    createAuthInterceptor,
  };
};

export const provideAuthContext = (authContext: AuthContext): void => {
  setContext<AuthContext>(AuthContextKey, authContext);
};

export const useAuthContext = (): AuthContext => getContext(AuthContextKey);

/**
 * Extract the `code` query parameter from the current URL and immediately
 * strip it from the browser's address bar to mitigate the code from leaking
 * via referrer headers, browser history, or error reporting.
 *
 * Returns the code string if present, or undefined otherwise.
 */
const extractAndStripAuthCode = (): string | undefined => {
  const url = new URL(window.location.href);
  const code = url.searchParams.get('code');

  if (!code) {
    return undefined;
  }

  // Remove the code from the URL before any other processing.
  url.searchParams.delete('code');
  window.history.replaceState({}, document.title, url.toString());

  return code;
};
